import Service from '@ember/service';

import { service } from '@ember/service';
import { load } from 'ember-async-data';

import uiPageConfig from 'rise-ui/config/ui-page.js';

/**
 * Queries/caches ui-pages by location.
 * Prevents multiple outstanding requests for same location, allows caching by
 * location, and refreshes model when getting from expired cache.
 * @name UiPagesService
 */
export default class extends Service {
  @service ssr;
  @service store;

  outstandingPageRequests = new Map();
  pages = new Map();

  locationTransform(location) {
    return (uiPageConfig.locations || {})[location]
      || uiPageConfig.locationTransform(location);
  }

  is(page, location) {
    return page && page.location === this.locationTransform(location) || false;
  }

  /**
   * Fetch or return cached ui-page of given location
   * @param {string} location
   * @param {boolean} fetch False to prevent fetch (default: true)
   * @param {boolean|null} [backgroundReload] False to prevent backgroundReload (default: null)
   * @return {ObjectProxy}
   */
  get(location, fetch = true, backgroundReload = null) {
    if (this.store.isDestroyed) {
      return Promise.reject();
    }
    // Check first for outstanding request for given location
    let result = this.outstandingPageRequests.get(location);
    if (!result) {
      // Search for cached page for location
      this.store.peekAll('ui-page').every(page => {
        if (this.is(page, location)) {
          result = this.fetch(location, backgroundReload);
          return false; // Exit loop
        }
        return true; // Continue loop
      });
    }
    if (!result && fetch) {
      // Fetch page for non-cached location
      result = this.fetch(location);
    }
    return result;
  }

  /**
   * Fetch ui-page of given location
   * @param {string} location
   * @param {boolean|null} [backgroundReload] False to prevent backgroundReload (default: null)
   * @return {ObjectProxy}
   */
  fetch(location, backgroundReload = null) {
    if (this.store.isDestroyed) {
      return Promise.reject();
    }
    // Check first for outstanding request for given location
    let result = this.outstandingPageRequests.get(location);
    if (!result) {
      // Query for page of given location
      result = this.store.findRecord('ui-page', location, { backgroundReload })
        .finally(() => this.outstandingPageRequests.delete(location));
      if (this.ssr.isSsr) {
        this.ssr.deferRendering(result.catch(() => {}));
      }
      result = load(result);
      this.outstandingPageRequests.set(location, result);
    }
    return result;
  }
}
